import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { BreathingAura } from '../components/BreathingAura';
import { motion } from 'framer-motion';
import { Heart, TrendingUp, MessageCircle, PenLine, LogOut } from 'lucide-react';
import { toast } from 'sonner';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const data = await api.getUserStatus();
      setStatus(data);
    } catch (error) {
      toast.error('Failed to load status');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const getBurnoutColor = (risk) => {
    switch (risk) {
      case 'High': return 'text-destructive';
      case 'Moderate': return 'text-accent';
      default: return 'text-primary';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-primary text-xl font-serif">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <BreathingAura variant="calm" />
      
      <div className="relative z-10">
        <div className="container mx-auto px-6 py-8 max-w-7xl">
          <div className="flex justify-between items-start mb-12">
            <div>
              <h1 className="text-5xl md:text-6xl font-serif font-light text-foreground tracking-tight mb-2">
                Welcome back{user?.displayName ? `, ${user.displayName.split(' ')[0]}` : ''}
              </h1>
              <p className="text-muted-foreground text-lg">How are you feeling today?</p>
            </div>
            <Button
              data-testid="logout-button"
              onClick={handleLogout}
              variant="ghost"
              className="rounded-full"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-card/50 backdrop-blur-xl border border-white/5 rounded-3xl p-8 hover:border-white/10 transition-colors duration-500"
            >
              <Heart className="w-8 h-8 text-primary mb-4" />
              <div className="text-3xl font-light text-foreground mb-2">
                {status?.current_mood || 'Neutral'}
              </div>
              <div className="text-sm text-muted-foreground">Current Mood</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-card/50 backdrop-blur-xl border border-white/5 rounded-3xl p-8 hover:border-white/10 transition-colors duration-500"
            >
              <PenLine className="w-8 h-8 text-secondary mb-4" />
              <div className="text-3xl font-light text-foreground mb-2">
                {status?.entries_today || 0}/3
              </div>
              <div className="text-sm text-muted-foreground">Today's Check-ins</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-card/50 backdrop-blur-xl border border-white/5 rounded-3xl p-8 hover:border-white/10 transition-colors duration-500"
            >
              <TrendingUp className="w-8 h-8 text-accent mb-4" />
              <div className="text-3xl font-light text-foreground mb-2">
                {status?.average_sentiment?.toFixed(1) || '5.0'}/10
              </div>
              <div className="text-sm text-muted-foreground">Average Sentiment</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-card/50 backdrop-blur-xl border border-white/5 rounded-3xl p-8 hover:border-white/10 transition-colors duration-500"
            >
              <div className={`text-3xl font-light mb-2 ${getBurnoutColor(status?.burnout_risk)}`}>
                {status?.burnout_risk || 'Low'}
              </div>
              <div className="text-sm text-muted-foreground">Burnout Risk</div>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              onClick={() => navigate('/checkin')}
              data-testid="navigate-checkin"
              className="bg-card/50 backdrop-blur-xl border border-white/5 rounded-3xl p-12 hover:border-primary/50 transition-all duration-500 cursor-pointer group"
            >
              <PenLine className="w-12 h-12 text-primary mb-6 group-hover:scale-110 transition-transform" />
              <h2 className="text-3xl font-serif font-light text-foreground mb-3">Daily Check-in</h2>
              <p className="text-muted-foreground leading-relaxed">
                Share how you're feeling through voice or text. Get personalized insights and coping strategies.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              onClick={() => navigate('/chat')}
              data-testid="navigate-chat"
              className="bg-card/50 backdrop-blur-xl border border-white/5 rounded-3xl p-12 hover:border-secondary/50 transition-all duration-500 cursor-pointer group"
            >
              <MessageCircle className="w-12 h-12 text-secondary mb-6 group-hover:scale-110 transition-transform" />
              <h2 className="text-3xl font-serif font-light text-foreground mb-3">Chat with AURA</h2>
              <p className="text-muted-foreground leading-relaxed">
                Talk to your AI companion. Get support, guidance, and understanding anytime you need it.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              onClick={() => navigate('/trends')}
              data-testid="navigate-trends"
              className="bg-card/50 backdrop-blur-xl border border-white/5 rounded-3xl p-12 hover:border-accent/50 transition-all duration-500 cursor-pointer group md:col-span-2"
            >
              <TrendingUp className="w-12 h-12 text-accent mb-6 group-hover:scale-110 transition-transform" />
              <h2 className="text-3xl font-serif font-light text-foreground mb-3">Emotional Trends</h2>
              <p className="text-muted-foreground leading-relaxed">
                Visualize your emotional journey. Discover patterns and track your progress over time.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;